import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { listAPI, categoryAPI } from "../services/api";
import { usePageTitle } from "../hooks/usePageTitle";
import ReactMarkdown from "react-markdown";

const CategoryPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sortBy, setSortBy] = useState("recent");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 21; // 21 items per page (3x7 grid)

  // Dynamic page title
  usePageTitle(category?.name || "Category");

  useEffect(() => {
    loadCategory();
  }, [slug]);

  useEffect(() => {
    if (category) {
      setCurrentPage(0); // Reset to first page when sort changes
      loadLists(0);
    }
  }, [category, sortBy]);

  useEffect(() => {
    if (category) {
      loadLists(currentPage);
    }
  }, [currentPage]);

  const loadCategory = async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await categoryAPI.getCategoryBySlug(slug);
      setCategory(response.data);
    } catch (error) {
      console.error("Failed to load category:", error);
      setError(true);
      setLoading(false);
    }
  };

  const loadLists = async (page) => {
    setLoading(true);
    try {
      const response = await listAPI.getListsByCategory(
        category.id,
        page,
        pageSize,
        sortBy,
      );
      setLists(response.data.content);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
      setCurrentPage(response.data.number);

      // Scroll to top smoothly when page changes
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Failed to load lists:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is small
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page, last page, current page, and nearby pages
      if (currentPage <= 2) {
        // Near the start
        for (let i = 0; i < 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages - 1);
      } else if (currentPage >= totalPages - 3) {
        // Near the end
        pages.push(0);
        pages.push("...");
        for (let i = totalPages - 4; i < totalPages; i++) pages.push(i);
      } else {
        // In the middle
        pages.push(0);
        pages.push("...");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("...");
        pages.push(totalPages - 1);
      }
    }

    return pages;
  };

  // Show error page if category not found
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          {/* Error Icon */}
          <div className="mb-8">
            <svg
              className="w-24 h-24 mx-auto text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
              />
            </svg>
          </div>

          {/* Error Message */}
          <h2 className="text-3xl font-bold text-white mb-4">
            Category Not Found
          </h2>
          <p className="text-gray-400 mb-8">
            The category you're looking for doesn't exist or has been removed.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate("/")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition"
            >
              Browse All Lists
            </button>
            <button
              onClick={() => navigate(-1)}
              className="bg-gray-700 hover:bg-gray-600 text-white font-semibold px-6 py-3 rounded-lg transition"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading && !category) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
        <div className="text-lg text-white">Loading category...</div>
      </div>
    );
  }

  // Show category not loaded (shouldn't happen, but safety check)
  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Category Header */}
      <div className="mb-8">
        <Link
          to="/"
          className="text-blue-400 hover:text-blue-300 mb-4 inline-block"
        >
          ← Back to Home
        </Link>
        <h1 className="text-4xl font-bold text-white mb-2">{category.name}</h1>
        {category.description && (
          <p className="text-gray-400">{category.description}</p>
        )}
        <p className="text-gray-500 mt-2">
          {totalElements} {totalElements === 1 ? "list" : "lists"} in this
          category
        </p>
      </div>

      {/* Sort Options */}
      <div className="mb-6 flex justify-end">
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-4 py-2 bg-dark-card border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
        >
          <option value="recent">Recently Added</option>
          <option value="popularity">Most Popular</option>
          <option value="most-items">Most Items</option>
          <option value="fewest-items">Fewest Items</option>
          <option value="az">A → Z</option>
          <option value="za">Z → A</option>
        </select>
      </div>

      {/* Lists Grid */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mb-4"></div>
          <div className="text-gray-400">Loading lists...</div>
        </div>
      ) : lists.length === 0 ? (
        <div className="text-center py-12">
          <svg
            className="w-16 h-16 mx-auto text-gray-600 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-gray-400 text-lg">
            No lists found in this category yet.
          </p>
          <Link
            to="/"
            className="inline-block mt-4 text-blue-400 hover:text-blue-300 underline"
          >
            Browse all lists
          </Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lists.map((list) => (
              <Link
                key={list.id}
                to={`/list/${list.slug}`}
                className="bg-dark-card hover:bg-dark-hover border border-gray-700 p-6 rounded-lg transition group"
              >
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition">
                  {list.title}
                </h3>

                {list.description && (
                  <div className="text-gray-400 text-sm mb-4 line-clamp-2 prose prose-invert prose-sm">
                    <ReactMarkdown
                      components={{
                        p: ({ node, ...props }) => <span {...props} />,
                        strong: ({ node, ...props }) => (
                          <strong className="text-white" {...props} />
                        ),
                        em: ({ node, ...props }) => <em {...props} />,
                      }}
                    >
                      {list.description}
                    </ReactMarkdown>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mb-4">
                  {list.categories
                    .filter((cat) => cat.name.toLowerCase() !== "ranked")
                    .map((cat) => (
                      <span
                        key={cat.id}
                        className="px-2 py-1 bg-blue-600/20 text-blue-400 text-xs rounded"
                      >
                        {cat.name}
                      </span>
                    ))}
                  {list.isRanked && (
                    <span className="px-2 py-1 bg-yellow-600/20 text-yellow-400 text-xs rounded">
                      Ranked
                    </span>
                  )}
                </div>

                <div className="flex justify-between items-center text-sm text-gray-400 mb-2">
                  <span>{list.totalItems} items</span>
                  <div className="flex items-center gap-1.5">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    <span>
                      {list.popularity}{" "}
                      {list.popularity === 1 ? "play" : "plays"}
                    </span>
                  </div>
                </div>

                {/* Progress Indicator */}
                {list.isCompleted && (
                  <div className="flex items-center gap-1.5 text-green-400 text-sm font-medium">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>Completed</span>
                  </div>
                )}
                {!list.isCompleted && list.isStarted && (
                  <div className="flex items-center gap-1.5 text-orange-400 text-sm font-medium">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>
                      In Progress ({list.guessedCount}/{list.totalItems})
                    </span>
                  </div>
                )}
              </Link>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 bg-dark-card border border-gray-700 rounded-lg p-4">
              {/* Page Info */}
              <div className="text-sm text-gray-400">
                Page {currentPage + 1} of {totalPages}
              </div>

              {/* Page Navigation */}
              <div className="flex items-center gap-2">
                {/* Previous Button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                  className="px-3 py-2 bg-dark-hover border border-gray-600 rounded-lg text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-700 hover:border-blue-500 transition text-sm font-medium"
                  aria-label="Previous page"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>

                {/* Page Numbers */}
                <div className="hidden sm:flex items-center gap-1">
                  {getPageNumbers().map((pageNum, index) =>
                    pageNum === "..." ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-3 py-2 text-gray-500"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                          currentPage === pageNum
                            ? "bg-blue-600 text-white border border-blue-500"
                            : "bg-dark-hover border border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-blue-500"
                        }`}
                      >
                        {pageNum + 1}
                      </button>
                    ),
                  )}
                </div>

                {/* Mobile: Current Page Only */}
                <div className="sm:hidden px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium border border-blue-500">
                  {currentPage + 1}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages - 1}
                  className="px-3 py-2 bg-dark-hover border border-gray-600 rounded-lg text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-700 hover:border-blue-500 transition text-sm font-medium"
                  aria-label="Next page"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>

              {/* Quick Jump (Desktop only) */}
              <div className="hidden lg:flex items-center gap-2">
                <label htmlFor="pageJump" className="text-sm text-gray-400">
                  Go to:
                </label>
                <select
                  id="pageJump"
                  value={currentPage}
                  onChange={(e) => handlePageChange(parseInt(e.target.value))}
                  className="px-3 py-2 bg-dark-hover border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 text-sm"
                >
                  {Array.from({ length: totalPages }, (_, i) => (
                    <option key={i} value={i}>
                      Page {i + 1}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CategoryPage;
